import {
    BrowserEdges,
    blendAtTop,
    type EdgePalette,
    viewportTop,
} from "@wags/ui/browser-edges.tsx";
import {
    blendAcross,
    EDGE_BLEND_PX,
    EDGE_COLORS,
} from "@wags/ui/edge-color.ts";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type { SearchTerminalContext } from "@wags/discovery/index.ts";
import { DEFAULT_MAX_DISTANCE_MILES } from "@wags/discovery/pure.ts";
import { usePostHog } from "posthog-js/react";
import {
    useUnsafeEffect,
    useUnsafeLayoutEffect,
} from "@wags/tanstack-start/react";
import { lazy, Suspense, useRef, useState } from "react";
import {
    getResolvedLocation,
    hasActiveFindFilters,
} from "../routes/browse-route-helpers";
import { FindFeedContent, FindFeedHeaders } from "./FindFeedSection";
import { FindLocationPrompt } from "./FindLocationPrompt";
import { FeedSearchProvider } from "../search-controls/feed-search-control";
import { shouldFinishFindFeedWordmarkOnPaint } from "./find-load-failure";
import { useFindFeed } from "./findFeedData";
import {
    bindFindLocation,
    type FindRoutePath,
    type FindSearchPromise,
    type PendingFindFilters,
} from "../routes/find-route";
import { type FindSearch } from "../routes/find-route.contract.ts";
import { resolveFindLocationOrigin } from "../routes/find-location-origin";
import { FindLocationChip } from "./FindLocationChip";
import {
    type InitialGeoLocation,
    type LocationSelection,
    persistLocationSelection,
    rememberLocationSelection,
    useSelectedLocation,
} from "@/features/viewer-location";
import { useElementAboveViewportTop } from "@wags/tanstack-start/browser";
import { useIsMobileViewport } from "@wags/tanstack-start/react";
import {
    finishHeaderWordmarkSearchOnPaint,
    withHeaderWordmarkSearchLoading,
} from "@/lib/core/header-wordmark-motion";
import {
    createFindFilterSubmissionDraft,
    findSearchParamsForCommittedChange,
    findSearchStateAfterChange,
} from "./find-search-submission";
import { registerVisibleFeedSnapshot } from "@/features/matching";
import {
    createSearchNavigationContext,
    resolveSearchNavigationContext,
    type SearchSortValue,
    shouldRecordFindUrlSearchSubmission,
    trackSearchSubmission,
    trackSearchUrlNavigation,
} from "@/features/discovery";

const FindResultsContent = lazy(() =>
    import("./FindResultsSection").then((mod) => ({
        default: mod.FindResultsContent,
    })),
);
const FindResultsHeaders = lazy(() =>
    import("./FindResultsSection").then((mod) => ({
        default: mod.FindResultsHeaders,
    })),
);
const FindModeFrame = lazy(() =>
    import("./find-page-mode-frame").then((mod) => ({
        default: mod.FindModeFrame,
    })),
);

export function FindPage({
    routePath,
    estimatedLocation,
    searchCorrelation,
    searchParams: urlSearch,
    searchPromise,
    feedReadyPromise,
    visible,
}: {
    routePath: FindRoutePath;
    estimatedLocation?: InitialGeoLocation | null;
    searchCorrelation: SearchTerminalContext;
    searchParams: FindSearch;
    searchPromise: FindSearchPromise | undefined;
    feedReadyPromise: Promise<void> | undefined;
    visible: boolean;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const posthog = usePostHog();
    const selectedLocation = useSelectedLocation();
    const searchParams = bindFindLocation(
        urlSearch,
        estimatedLocation,
        selectedLocation,
    );
    const isResultsMode = hasActiveFindFilters(searchParams);
    const [entryResultsMode] = useState(isResultsMode);
    const [modeFrameReady, setModeFrameReady] = useState(
        isResultsMode !== entryResultsMode,
    );
    useUnsafeEffect(() => {
        if (isResultsMode !== entryResultsMode) {
            setModeFrameReady(true);
        }
    }, [isResultsMode, entryResultsMode]);
    const resolvedLocation = getResolvedLocation(searchParams);
    const locationOrigin = resolveFindLocationOrigin(
        searchParams,
        estimatedLocation,
        selectedLocation,
    );
    const historyCorrelation =
        typeof location.state === "object" && location.state !== null
            ? resolveSearchNavigationContext(location.state)
            : null;
    const observedNavigationRef = useRef<string | null>(null);
    const recordUrlNavigation = (element: HTMLSpanElement | null) => {
        if (!element) return;
        if (
            !shouldRecordFindUrlSearchSubmission({
                isResultsMode,
                navigationContext: historyCorrelation,
                observedContext: searchCorrelation,
            }) ||
            observedNavigationRef.current === searchCorrelation.submissionId
        )
            return;
        observedNavigationRef.current = searchCorrelation.submissionId;
        trackSearchUrlNavigation(searchCorrelation);
    };

    const searchActionStateRef = useRef({
        locationIdentity: `${location.href}:${searchCorrelation.submissionId}`,
        search: searchParams,
        renderedSearch: searchParams,
        context: searchCorrelation,
    });
    const locationIdentity = `${location.href}:${searchCorrelation.submissionId}`;
    useUnsafeLayoutEffect(() => {
        if (
            visible &&
            locationOrigin?.status === "confirmed" &&
            urlSearch.lat != null &&
            urlSearch.lng != null
        ) {
            rememberLocationSelection({
                lat: urlSearch.lat,
                lng: urlSearch.lng,
            });
        }
        const actionState = searchActionStateRef.current;
        if (
            actionState.locationIdentity === locationIdentity &&
            actionState.search !== actionState.renderedSearch
        )
            return;
        searchActionStateRef.current = {
            locationIdentity,
            search: searchParams,
            renderedSearch: searchParams,
            context: searchCorrelation,
        };
    }, [
        locationIdentity,
        locationOrigin?.status,
        searchParams,
        searchCorrelation,
        urlSearch,
        visible,
    ]);

    const [locationPromptOpen, setLocationPromptOpen] = useState(false);
    const pendingFiltersRef = useRef<PendingFindFilters | null>(null);

    const feed = useFindFeed({ resolvedLocation, isResultsMode });

    const feedSnapshotRef = useRef({ isResultsMode, rows: feed.nonEmptyRows });
    const updateFeedSnapshot = (node: HTMLSpanElement | null) => {
        if (!node) return;
        feedSnapshotRef.current = {
            isResultsMode,
            rows: feed.nonEmptyRows,
        };
    };
    const [ownFeedSnapshot] = useState(() => (node: HTMLSpanElement | null) => {
        if (!node) return;
        return registerVisibleFeedSnapshot(() => {
            const { isResultsMode: resultsMode, rows } =
                feedSnapshotRef.current;
            if (resultsMode) return [];
            const byId = new Map();
            for (const row of rows) {
                for (const d of row.dogListings) {
                    if (!byId.has(d.id)) {
                        byId.set(d.id, {
                            id: d.id,
                            name: d.name,
                            imageUrl: d.imageUrls[0] ?? null,
                            distanceMiles: d.distanceMiles,
                            breedLabel: d.breeds[0]?.name ?? null,
                        });
                    }
                }
            }
            return [...byId.values()];
        });
    });

    const [moreResultsEl, setMoreResultsEl] = useState<HTMLDivElement | null>(
        null,
    );

    const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
    const feedCardCovers = useElementAboveViewportTop(contentEl);

    const [footerSentinelEl, setFooterSentinelEl] =
        useState<HTMLDivElement | null>(null);
    const isMobile = useIsMobileViewport();

    const resultsHeaderOnWhite = useElementAboveViewportTop(moreResultsEl, {
        enabled: isResultsMode,
    });
    const [resultsTopEl, setResultsTopEl] = useState<HTMLDivElement | null>(
        null,
    );
    const resultsUnderHeader = useElementAboveViewportTop(resultsTopEl, {
        enabled: isResultsMode,
    });

    const findEdgePalette = (): EdgePalette => {
        const pageTop = blendAtTop(
            isResultsMode
                ? EDGE_COLORS.malteseWhite
                : EDGE_COLORS.dobermanBlack,
            EDGE_COLORS.trueWhite,
            isResultsMode ? moreResultsEl : contentEl,
        );
        const footerTop = viewportTop(footerSentinelEl);
        return {
            top: blendAcross(
                pageTop,
                EDGE_COLORS.dobermanBlack,
                EDGE_BLEND_PX - footerTop,
            ),
            bottom: blendAcross(
                EDGE_COLORS.trueWhite,
                EDGE_COLORS.dobermanBlack,
                window.innerHeight - footerTop,
            ),
        };
    };

    const requireLocationThen = (nextParams: PendingFindFilters | null) => {
        pendingFiltersRef.current = nextParams;
        setLocationPromptOpen(true);
    };

    const handleSearchParamsChange = (
        nextSearchParams: FindSearch,
        terminalContext?: SearchTerminalContext,
        { resetScroll = true }: { resetScroll?: boolean } = {},
    ) => {
        const actionState = searchActionStateRef.current;
        const changedSearchParams = findSearchStateAfterChange(
            searchParams,
            nextSearchParams,
            actionState.search,
        );
        const filterContext = terminalContext
            ? null
            : createFindFilterSubmissionDraft(
                  actionState.context,
                  actionState.search,
                  changedSearchParams,
              );
        if (
            !terminalContext &&
            !filterContext &&
            changedSearchParams.locationSource ===
                actionState.search.locationSource
        )
            return;
        const context =
            terminalContext ??
            (filterContext
                ? trackSearchSubmission(filterContext)
                : actionState.context);
        const nextRouteSearch =
            findSearchParamsForCommittedChange(changedSearchParams);
        searchActionStateRef.current = {
            locationIdentity,
            search: nextRouteSearch,
            renderedSearch: actionState.renderedSearch,
            context,
        };
        const {
            lat: _lat,
            lng: _lng,
            page: _page,
            ...filters
        } = nextRouteSearch;
        if (!terminalContext && filterContext) {
            posthog.capture("search_params_changed", filters);
        }
        withHeaderWordmarkSearchLoading(() =>
            navigate({
                to: routePath,
                search: nextRouteSearch,
                state: {
                    searchCorrelation: createSearchNavigationContext(context),
                },
                resetScroll,
            }),
        );
    };

    const handleSortChange = (sortBy: SearchSortValue) => {
        handleSearchParamsChange({ ...searchParams, sortBy }, undefined, {
            resetScroll: false,
        });
    };

    const handleLocationSelected = (location: LocationSelection) => {
        persistLocationSelection(location);
        setLocationPromptOpen(false);
        const filters = pendingFiltersRef.current;
        pendingFiltersRef.current = null;
        const nextSearchParams = {
            ...searchParams,
            lat: location.lat,
            lng: location.lng,
            locationSource: undefined,
            maxDistanceMiles:
                searchParams.maxDistanceMiles ?? DEFAULT_MAX_DISTANCE_MILES,
            ...(filters ?? {}),
            page: 0,
        };
        if (filters || hasActiveFindFilters(searchParams)) {
            handleSearchParamsChange(nextSearchParams);
            return;
        }
        withHeaderWordmarkSearchLoading(() =>
            navigate({ to: routePath, search: nextSearchParams }),
        );
    };

    const handleClearFeedContext = () => {
        withHeaderWordmarkSearchLoading(() =>
            navigate({
                to: routePath,
                search: {
                    lat: searchParams.lat,
                    lng: searchParams.lng,
                    locationSource: searchParams.locationSource,
                    page: 0,
                },
            }),
        );
    };

    const mode = isResultsMode ? "results" : "feed";
    const modeClassName = isResultsMode
        ? "w-full min-h-dvh flex flex-col items-center bg-maltese-white"
        : "relative bg-maltese-white";
    const modeBody = isResultsMode ? (
        <FindResultsContent
            searchParams={searchParams}
            searchPromise={searchPromise}
            correlation={searchCorrelation}
            headerOnWhite={resultsHeaderOnWhite}
            headerDivided={resultsUnderHeader}
            moreResultsRef={setMoreResultsEl}
            resultsTopRef={setResultsTopEl}
            onSearchParamsChange={handleSearchParamsChange}
            onSortChange={handleSortChange}
            onRequireLocation={() => requireLocationThen(null)}
        />
    ) : (
        <FindFeedContent
            searchParams={searchParams}
            resolvedLocation={resolvedLocation}
            feed={feed}
            feedReadyPromise={feedReadyPromise}
            heroCovered={feedCardCovers}
            contentRef={setContentEl}
            footerSentinelRef={setFooterSentinelEl}
            onSearchParamsChange={handleSearchParamsChange}
            onRequireLocation={requireLocationThen}
            locationChip={
                locationOrigin ? (
                    <FindLocationChip
                        origin={locationOrigin}
                        onChange={() => {
                            posthog.capture("find_location_chip_clicked", {
                                status: locationOrigin.status,
                            });
                            requireLocationThen(null);
                        }}
                    />
                ) : null
            }
        />
    );

    return (
        <FeedSearchProvider>
            <span ref={updateFeedSnapshot} hidden />
            <span ref={ownFeedSnapshot} hidden />
            {shouldFinishFindFeedWordmarkOnPaint({
                isResultsMode,
                feedLoadUi: feed.feedLoadUi,
                findFeedIsFetching: feed.findFeedIsFetching,
            }) && (
                <span
                    key={searchCorrelation.submissionId}
                    ref={finishHeaderWordmarkSearchOnPaint}
                    hidden
                />
            )}
            {isResultsMode && !historyCorrelation && (
                <span ref={recordUrlNavigation} hidden />
            )}
            {isResultsMode ? (
                <Suspense fallback={null}>
                    <FindResultsHeaders
                        searchParams={searchParams}
                        headerOnWhite={resultsHeaderOnWhite}
                        isMobile={isMobile}
                        feedContextLabel={searchParams.feedContext}
                        onSearchParamsChange={handleSearchParamsChange}
                        onClearFeedContext={handleClearFeedContext}
                    />
                </Suspense>
            ) : (
                <FindFeedHeaders
                    searchParams={searchParams}
                    headerOnWhite={feedCardCovers}
                    footerSentinelEl={footerSentinelEl}
                    isMobile={isMobile}
                    onSearchParamsChange={handleSearchParamsChange}
                />
            )}

            {modeFrameReady ? (
                <Suspense fallback={<div className={modeClassName} />}>
                    <FindModeFrame mode={mode} className={modeClassName}>
                        {modeBody}
                    </FindModeFrame>
                </Suspense>
            ) : (
                <Suspense fallback={<div className={modeClassName} />}>
                    <div className={modeClassName}>{modeBody}</div>
                </Suspense>
            )}
            {locationPromptOpen ? (
                <FindLocationPrompt
                    open={locationPromptOpen}
                    onOpenChange={setLocationPromptOpen}
                    lat={searchParams.lat}
                    lng={searchParams.lng}
                    onLocationSelected={handleLocationSelected}
                />
            ) : null}
            {visible ? (
                <BrowserEdges
                    initial={{
                        top: EDGE_COLORS.dobermanBlack,
                        bottom: EDGE_COLORS.trueWhite,
                    }}
                    paletteAt={findEdgePalette}
                    fade={!isResultsMode}
                />
            ) : null}
        </FeedSearchProvider>
    );
}
